import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Globe, ArrowRight, Quote } from "lucide-react";
import Hero from "../components/site/Hero";
import TrustMarquee from "../components/site/Marquee";
import { FeatureCard, CardGrid } from "../components/site/FeatureCard";
import { SectionHeader, Reveal } from "../components/site/Reveal";
import DashboardPreview from "../components/site/DashboardPreview";
import HubDiagram from "../components/site/HubDiagram";
import CloudArchitecture from "../components/site/CloudArchitecture";
import SecurityLayers from "../components/site/SecurityLayers";
import TechArchitecture from "../components/site/TechArchitecture";
import Timeline from "../components/site/Timeline";
import LogoCloud from "../components/site/LogoCloud";
import ContactForm from "../components/site/ContactForm";
import CTASection from "../components/site/CTASection";
import ArticleCard from "../components/site/ArticleCard";
import InfraCarousel from "../components/site/InfraCarousel";
import { ButtonPrimary, ButtonSecondary, ButtonGhostLight } from "../components/site/Buttons";
import {
  QUE_HACEMOS, MODULOS, SERVICIOS, ECOSISTEMA, INTEGRACIONES, EMPRESA_VALORES,
  SECTOR_ITEMS, CASOS_DE_USO, ARTICULOS, CONTACT_INFO,
} from "../data/site";

const IMG_EMPRESA =
  "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200&auto=format&fit=crop";

export default function Home() {
  return (
    <>
      <Hero />
      <TrustMarquee />

      {/* Qué hacemos */}
      <section id="soluciones" className="py-20 lg:py-24" data-testid="section-que-hacemos">
        <div className="wrap">
          <SectionHeader
            eyebrow="Qué hacemos"
            title="Tecnología diseñada para resolver problemas reales."
            description="En DASOFT desarrollamos soluciones tecnológicas orientadas a mejorar procesos, automatizar operaciones y facilitar la toma de decisiones."
          />
          <CardGrid className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {QUE_HACEMOS.map((c) => (
              <FeatureCard key={c.title} {...c} />
            ))}
          </CardGrid>
        </div>
      </section>

      {/* CoopSys — producto principal */}
      <section id="coopsys" className="relative overflow-hidden py-20 lg:py-24" data-testid="section-coopsys">
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[720px] -translate-x-1/2 rounded-full bg-brand/10 blur-[130px]" aria-hidden="true" />
        <div className="wrap relative">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">
                Producto principal
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 text-4xl font-extrabold sm:text-5xl">
                CoopSys <span className="text-gradient">CoreBank</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-lg font-medium text-foreground/90">
                La plataforma integral para la gestión de cooperativas de ahorro, crédito y servicios múltiples.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                Centraliza las operaciones financieras, administrativas y contables de tu cooperativa en una
                plataforma moderna, segura y accesible desde la nube.
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <ButtonPrimary to="/coopsys" testid="coopsys-know-cta">Conocer CoopSys</ButtonPrimary>
                <ButtonSecondary to="/contacto" testid="coopsys-demo-cta">Solicitar Demo</ButtonSecondary>
              </div>
            </Reveal>
          </div>
          <div className="relative mt-14">
            <div className="pointer-events-none absolute inset-x-8 -top-6 h-40 rounded-full bg-brand/20 blur-[90px]" aria-hidden="true" />
            <DashboardPreview className="relative mx-auto max-w-5xl" />
          </div>
        </div>
      </section>

      {/* Módulos */}
      <section id="modulos" className="py-20 lg:py-24" data-testid="section-modulos">
        <div className="wrap">
          <SectionHeader
            eyebrow="CoopSys CoreBank"
            title="La plataforma completa, módulo por módulo."
            description="Cada área de tu cooperativa cuenta con herramientas especializadas que trabajan sobre una misma base de datos."
          />
          <CardGrid className="mt-14 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {MODULOS.map((m) => (
              <FeatureCard key={m.title} {...m} compact />
            ))}
          </CardGrid>
        </div>
      </section>

      {/* Una plataforma. Todo conectado. */}
      <section id="plataforma" className="py-20 lg:py-24" data-testid="section-plataforma">
        <div className="wrap">
          <SectionHeader
            align="center"
            eyebrow="Integración total"
            title="Una plataforma. Todo conectado."
            description="Cada operación fluye hacia el núcleo y regresa como información útil para toda la organización."
          />
          <div className="mt-14">
            <HubDiagram />
          </div>
        </div>
      </section>

      {/* Cloud */}
      <section id="cloud" className="py-20 lg:py-24" data-testid="section-cloud">
        <div className="wrap">
          <CloudArchitecture />
        </div>
      </section>

      {/* Seguridad */}
      <section id="seguridad" className="py-20 lg:py-24" data-testid="section-seguridad">
        <div className="wrap">
          <SecurityLayers />
        </div>
      </section>

      {/* Technology behind the business */}
      <section id="tecnologia" className="py-10 lg:py-14" data-testid="section-tecnologia">
        <div className="wrap">
          <TechArchitecture />
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-20 lg:py-24" data-testid="section-servicios">
        <div className="wrap">
          <SectionHeader
            eyebrow="Servicios"
            title="Más que software. Un socio tecnológico."
            description="Acompañamos a tu organización en todo el ciclo: diseño, construcción, operación y evolución de sus soluciones."
          />
          <CardGrid className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICIOS.map((s) => (
              <FeatureCard key={s.title} {...s} />
            ))}
          </CardGrid>
          <Reveal delay={0.1} className="mt-10">
            <ButtonSecondary to="/servicios" testid="home-services-more-cta">
              Ver todos los servicios
            </ButtonSecondary>
          </Reveal>
        </div>
      </section>

      {/* Servicios técnicos — carrusel */}
      <section id="infraestructura" className="py-20 lg:py-24" data-testid="section-infraestructura">
        <div className="wrap">
          <InfraCarousel />
        </div>
      </section>

      {/* Ecosistema */}
      <section id="ecosistema" className="py-20 lg:py-24" data-testid="section-ecosistema">
        <div className="wrap">
          <SectionHeader
            eyebrow="Ecosistema DASOFT"
            title="Productos y soluciones que trabajan juntos."
          />
          <CardGrid className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ECOSISTEMA.map((p) => (
              <motion.article
                key={p.title}
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                className={`card-lift group relative overflow-hidden rounded-2xl border p-7 hover:shadow-card-hover ${p.featured
                  ? "border-brand/40 bg-gradient-to-br from-brand/10 via-card to-electric/5 md:col-span-2"
                  : "border-border bg-card hover:border-electric/40"
                  }`}
                data-testid={`ecosystem-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {p.featured && (
                  <span className="absolute right-5 top-5 rounded-full bg-brand px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-white">
                    Estrella
                  </span>
                )}
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand group-hover:border-electric/40 group-hover:text-electric">
                  <p.icon className="h-5 w-5" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </motion.article>
            ))}
          </CardGrid>
        </div>
      </section>

      {/* Integraciones */}
      <section id="integraciones" className="py-20 lg:py-24" data-testid="section-integraciones">
        <div className="wrap">
          <SectionHeader
            eyebrow="Integraciones"
            title="Conecta las herramientas que ya utilizas."
            description="Integramos tu plataforma con servicios financieros, canales de comunicación, fuentes de datos gubernamentales y burós de crédito."
          />
          <CardGrid className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INTEGRACIONES.map((c) => (
              <motion.article
                key={c.title}
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                className="card-lift rounded-2xl border border-border bg-card p-7 hover:border-electric/40 hover:shadow-card-hover"
                data-testid={`integration-card-${c.title.toLowerCase().replace(/[^a-z0-9/ ]+/g, "-").replace(/-+$/, "")}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                  <c.icon className="h-5 w-5" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{c.title}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.items.map((it) => (
                    <span key={it} className="rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted-foreground">
                      {it}
                    </span>
                  ))}
                </div>
              </motion.article>
            ))}
          </CardGrid>
        </div>
      </section>

      {/* Empresa */}
      <section id="empresa" className="py-20 lg:py-24" data-testid="section-empresa">
        <div className="wrap grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeader
              eyebrow="Empresa"
              title="Tecnología con experiencia y visión de futuro."
              description="DASOFT SRL es una empresa tecnológica enfocada en el desarrollo de software, infraestructura y soluciones digitales para organizaciones que necesitan operar de forma más eficiente, segura y conectada."
            />
            <div className="mt-10 flex flex-col gap-6">
              {EMPRESA_VALORES.map((v, i) => (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="group flex gap-5 rounded-2xl border border-transparent p-4 transition-all duration-300 hover:border-border hover:bg-card">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-brand group-hover:text-electric">
                      <v.icon className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-semibold">{v.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl border border-border shadow-card" data-testid="empresa-image">
              <img
                src={IMG_EMPRESA}
                alt="Equipo de DASOFT desarrollando soluciones tecnológicas"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B12]/90 via-[#070B12]/20 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-xl font-bold text-white sm:text-2xl">
                  Construimos tecnología para operaciones reales.
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">
                  DASOFT SRL — Technology Solutions
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sector cooperativo */}
      <section id="sector" className="py-10 lg:py-14" data-testid="section-sector">
        <div className="wrap">
          <div className="dark-section relative overflow-hidden rounded-[2rem] px-6 py-16 sm:px-12 lg:px-16">
            <div className="absolute inset-0 grid-bg opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_30%_50%,black,transparent)]" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-24 right-0 h-64 w-96 rounded-full bg-electric/20 blur-[110px]" aria-hidden="true" />
            <div className="relative grid items-center gap-12 lg:grid-cols-2">
              <div>
                <motion.span
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="eyebrow text-slate-400 before:bg-sky-400"
                >
                  Sector cooperativo
                </motion.span>
                <motion.h2
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.08 }}
                  className="mt-4 text-3xl font-bold text-white sm:text-4xl"
                >
                  Tecnología especializada en el sector cooperativo.
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.16 }}
                  className="mt-5 max-w-lg leading-relaxed text-slate-400"
                >
                  Con CoopSys, las cooperativas pueden centralizar sus operaciones financieras y administrativas
                  en una plataforma diseñada alrededor de sus necesidades.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.24 }}
                  className="mt-8"
                >
                  <ButtonPrimary to="/coopsys" testid="sector-coopsys-cta">Descubrir CoopSys</ButtonPrimary>
                </motion.div>
              </div>
              <motion.ul
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
                className="flex flex-wrap gap-2.5 lg:justify-end"
              >
                {SECTOR_ITEMS.map((item) => (
                  <motion.li
                    key={item}
                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } }}
                    className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm text-slate-200 backdrop-blur"
                  >
                    {item}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </div>
      </section>

      {/* Clientes */}
      <section id="clientes" className="py-20 lg:py-24" data-testid="section-clientes">
        <div className="wrap">
          <LogoCloud />
        </div>
      </section>

      {/* Casos de uso */}
      <section id="casos" className="py-20 lg:py-24" data-testid="section-casos">
        <div className="wrap">
          <SectionHeader
            eyebrow="Casos de uso"
            title="Una misma tecnología, muchas operaciones."
          />
          <CardGrid className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CASOS_DE_USO.map((c) => (
              <FeatureCard key={c.title} {...c} />
            ))}
          </CardGrid>
        </div>
      </section>

      {/* Proceso */}
      <section id="proceso" className="py-20 lg:py-24" data-testid="section-proceso">
        <div className="wrap">
          <Timeline />
        </div>
      </section>

      {/* Recursos / Blog */}
      <section id="recursos" className="py-20 lg:py-24" data-testid="section-recursos">
        <div className="wrap">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              eyebrow="Blog / Recursos"
              title="Ideas, tecnología y conocimiento"
            />
            <Reveal delay={0.1}>
              <Link
                to="/recursos"
                data-testid="blog-see-all-link"
                className="group inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:border-electric/50 hover:text-electric"
              >
                Ver todos los artículos
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <CardGrid className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ARTICULOS.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </CardGrid>
        </div>
      </section>

      <CTASection />

      {/* Contacto */}
      <section id="contacto" className="py-20 lg:py-24" data-testid="section-contacto">
        <div className="wrap grid gap-14 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <SectionHeader
              eyebrow="Contacto"
              title="Conversemos sobre tu próxima solución."
              description="Cuéntanos qué necesitas y un especialista te ayudará a definir el mejor camino."
            />
            <div className="mt-10 flex flex-col gap-4">
              <Reveal>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  data-testid="contact-info-email"
                  className="card-lift flex items-center gap-4 rounded-2xl border border-border bg-card p-5 hover:border-electric/40"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                    <Mail className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</span>
                    <span className="mt-0.5 block text-sm font-semibold">{CONTACT_INFO.email}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal>
                <a
                  href={`tel:18499370818`}
                  data-testid="contact-info-email"
                  className="card-lift flex items-center gap-4 rounded-2xl border border-border bg-card p-5 hover:border-electric/40"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                    <Mail className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contacto</span>
                    <span className="mt-0.5 block text-sm font-semibold">{CONTACT_INFO.phone}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="card-lift flex items-center gap-4 rounded-2xl border border-border bg-card p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                    <MapPin className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ubicación</span>
                    <span className="mt-0.5 block text-sm font-semibold">{CONTACT_INFO.location}</span>
                  </span>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="card-lift flex items-center gap-4 rounded-2xl border border-border bg-card p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                    <Globe className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sitio web</span>
                    <span className="mt-0.5 block text-sm font-semibold">{CONTACT_INFO.site}</span>
                  </span>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
